import { useEffect, useRef, useState } from "react";

type LockStatus = "idle" | "loading" | "active" | "waiting" | "lost";

type LockedBy = {
  userId: string;
  userName: string;
  userEmail?: string | null;
} | null;

type Params = {
  activePath?: string | null;
  userId?: string | null;
  userName?: string | null;
  userEmail?: string | null;
  enabled?: boolean;
  renewIntervalMs?: number;
};

export function useActiveEditorLock({
  activePath,
  userId,
  userName,
  userEmail,
  enabled = true,
  renewIntervalMs = 20_000,
}: Params) {
  const [status, setStatus] = useState<LockStatus>(
    enabled ? "loading" : "idle",
  );
  const [lockedBy, setLockedBy] = useState<LockedBy>(null);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const operationRef = useRef(0);
  const statusRef = useRef<LockStatus>(status);
  statusRef.current = status;

  const stateRef = useRef({
    activePath: activePath ?? null,
    userId: userId ?? null,
    userName: userName ?? null,
    userEmail: userEmail ?? null,
  });

  stateRef.current = {
    activePath: activePath ?? null,
    userId: userId ?? null,
    userName: userName ?? null,
    userEmail: userEmail ?? null,
  };

  const clearTimer = () => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
  };

  const shouldLock =
    enabled &&
    Boolean(
      stateRef.current.activePath &&
        stateRef.current.userId &&
        stateRef.current.userName,
    );

  const acquire = async () => {
    if (!shouldLock) {
      setStatus("idle");
      setLockedBy(null);
      return false;
    }

    setStatus(shouldLock ? "loading" : "idle");
    setLockedBy(null);

    const res = await fetch("/api/active-editor", {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({
        activePath: stateRef.current.activePath,
        userId: stateRef.current.userId,
        userName: stateRef.current.userName,
        userEmail: stateRef.current.userEmail,
      }),
    });

    const data = await res.json().catch(() => ({}));

    if (res.status === 423) {
      setStatus("waiting");
      setLockedBy(data?.lockedBy ?? null);
      return false;
    }

    if (!res.ok) {
      setStatus("lost");
      return false;
    }

    setStatus("active");
    setLockedBy(null);
    return true;
  };

  const renew = async () => {
    if (statusRef.current !== "active") return;
    if (!shouldLock) return;

    try {
      const res = await fetch("/api/active-editor", {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({
          activePath: stateRef.current.activePath,
          userId: stateRef.current.userId,
          userName: stateRef.current.userName,
          userEmail: stateRef.current.userEmail,
        }),
      });

      if (res.status === 423) {
        const data = await res.json().catch(() => ({}));
        setStatus("lost");
        setLockedBy(data?.lockedBy ?? null);
        clearTimer();
      } else if (!res.ok) {
        setStatus("lost");
        clearTimer();
      }
    } catch {
      // Ignore; TTL will naturally expire if we truly lost connectivity
    }
  };

  const release = async (path?: string | null, user?: string | null) => {
    clearTimer();
    const targetPath = path ?? stateRef.current.activePath;
    const targetUser = user ?? stateRef.current.userId;
    if (!targetPath || !targetUser) return;

    try {
      await fetch("/api/active-editor", {
        method: "DELETE",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({
          activePath: targetPath,
          userId: targetUser,
        }),
        // best-effort release on unload
        keepalive: true,
      });
    } catch {
      // Ignore
    }
  };

  const retry = async () => {
    const operationId = ++operationRef.current;
    const targetPath = stateRef.current.activePath;
    const targetUser = stateRef.current.userId;
    clearTimer();
    const ok = await acquire();
    if (operationId !== operationRef.current) {
      if (ok) void release(targetPath, targetUser);
      return false;
    }
    if (ok) {
      timerRef.current = setInterval(renew, renewIntervalMs);
    }
    return ok;
  };

  useEffect(() => {
    const operationId = ++operationRef.current;
    const currentPath = stateRef.current.activePath;
    const currentUser = stateRef.current.userId;

    if (!shouldLock) {
      setStatus("idle");
      setLockedBy(null);
      clearTimer();
      return undefined;
    }

    const start = async () => {
      clearTimer();
      const ok = await acquire();
      if (operationId !== operationRef.current) {
        if (ok) void release(currentPath, currentUser);
        return;
      }
      if (ok) {
        timerRef.current = setInterval(renew, renewIntervalMs);
      }
    };

    void start();

    const handleBeforeUnload = () => {
      void release(currentPath, currentUser);
    };

    window.addEventListener("beforeunload", handleBeforeUnload);

    return () => {
      operationRef.current += 1;
      clearTimer();
      window.removeEventListener("beforeunload", handleBeforeUnload);
      void release(currentPath, currentUser);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shouldLock, activePath, userId, userName, userEmail, renewIntervalMs]);

  return {
    status,
    lockedBy,
    retry,
    release,
  };
}
